import { PaymentStateColors } from '@/enums/paymentState';
import { ListResponse } from '@/interfaces/list';
import { Payment } from '@/interfaces/payment';
import { VariableType } from 'json-to-graphql-query';
import { EntityResource } from './entity.resource';

/**
 * Resource płatności
 *
 * @export
 * @class PaymentResource
 * @extends {EntityResource<Payment>}
 */
export class PaymentResource extends EntityResource<Payment> {
  public getFields = {
    id: true
  };

  public listFields = {
    id: true,
    created: true,
    data: true,
    date: true,
    amount: true,
    grant: true,
    contribution: true,
    number: true,
    externalId: true,
    state: true,
    error: true,
    pack: {
      id: true,
      structure: {
        id: true,
        name: true
      },
      status: true,
      externalId: true
    },
    packId: true,
    documentId: true,
    document: {
      id: true,
      procedureId: true,
      statusId: true,
      data: true
    }
  };

  protected listVariables = { filters: 'PaymentFilters!' };

  constructor() {
    super({ entityName: 'payment' });
  }

  /**
   * Pobiera listę płatności
   *
   * @param {*} filters
   * @param {boolean} [preventLoader]
   * @returns {Promise<ListResponse<Payment>>}
   * @memberof PaymentResource
   */
  public list(filters: any, preventLoader?: boolean): Promise<ListResponse<Payment>> {
    return super.list(filters, preventLoader).then(response => {
      response.list.forEach(payment => {
        payment.listClasses = PaymentStateColors[payment.state];
      });
      return response;
    });
  }

  /**
   * Anuluje wypłatę
   *
   * @param {number} id
   * @returns {Promise<boolean>}
   * @memberof PaymentResource
   */
  public cancel(id: number): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { id: 'Int!' },
        paymentCancel: {
          __args: { id: new VariableType('id') }
        }
      }
    });
    return this.mutation({ mutation, variables: { id } }, 'paymentCancel');
  }

  /**
   * Anuluje wypłatę w KSAT
   *
   * @param {number} id
   * @returns {Promise<boolean>}
   * @memberof PaymentResource
   */
  public cancelInKsat(id: number): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { id: 'Int!' },
        paymentCancelInKsat: {
          __args: { id: new VariableType('id') }
        }
      }
    });
    return this.mutation({ mutation, variables: { id } }, 'paymentCancelInKsat');
  }

  /**
   * Ponownie przypina płatność do paczki dokumentów w KSAT
   *
   * @param {number} id
   * @returns {Promise<boolean>}
   * @memberof PaymentResource
   */
  public reattachInKsat(id: number): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { id: 'Int!' },
        paymentReattachInKsat: {
          __args: { id: new VariableType('id') }
        }
      }
    });
    return this.mutation({ mutation, variables: { id } }, 'paymentReattachInKsat');
  }

  /**
   * Ponownie przesyła płatność do KSAT
   *
   * @param {{ id: number; }} variables
   * @returns {Promise<boolean>}
   * @memberof PaymentResource
   */
  public resendToKsat(variables: { id: number }): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { id: 'Int!' },
        paymentResendToKsat: {
          __args: { id: new VariableType('id') }
        }
      }
    });
    return this.mutation({ mutation, variables }, 'paymentResendToKsat');
  }

  /**
   * Aktualizuje dane kontrahenta w KSAT
   *
   * @param {{ id: number }} variables
   * @returns {Promise<boolean>}
   * @memberof PaymentResource
   */
  public updateContractor(variables: { id: number }): Promise<boolean> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { id: 'Int!' },
        paymentUpdateContractor: {
          __args: { id: new VariableType('id') }
        }
      }
    });
    return this.mutation({ mutation, variables }, 'paymentUpdateContractor');
  }
}

export const paymentResource = new PaymentResource();
